import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

/** Vlastní fakturační údaje uživatele — backend /api/moje-firmy. */
export interface MojeFirmaData {
  nazev: string;
  ico: string;
  dic: string;
  adresa: string;
  zapis_or: string;
  pobocka_nazev: string;
  pobocka_adresa: string;
  banka_nazev: string;
  banka_ucet: string;
  banka_iban: string;
  banka_swift: string;
  email: string;
  telefon: string;
  web: string;
  poznamka: string;
}

export interface MojeFirma extends MojeFirmaData {
  id: number;
  poradi: number;
}

export const EMPTY_FIRMA: MojeFirmaData = {
  nazev: '',
  ico: '',
  dic: '',
  adresa: '',
  zapis_or: '',
  pobocka_nazev: '',
  pobocka_adresa: '',
  banka_nazev: '',
  banka_ucet: '',
  banka_iban: '',
  banka_swift: '',
  email: '',
  telefon: '',
  web: '',
  poznamka: '',
};

const QK = ['moje-firmy'] as const;

export function useMojeFirmy(enabled = true) {
  return useQuery<MojeFirma[]>({
    queryKey: QK,
    queryFn: () => api.get<MojeFirma[]>('/moje-firmy'),
    enabled,
  });
}

export function useSaveMojeFirma() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number | null; data: MojeFirmaData }) =>
      id ? api.put<MojeFirma>(`/moje-firmy/${id}`, data) : api.post<MojeFirma>('/moje-firmy', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useDeleteMojeFirma() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/moje-firmy/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

/** Řádky pro zobrazení a kopírování — jen vyplněné položky, v pořadí jako na faktuře. */
export function firmaRows(f: MojeFirmaData): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [
    { label: 'Název', value: f.nazev },
    { label: 'Sídlo', value: f.adresa },
    { label: 'IČO', value: f.ico },
    { label: 'DIČ', value: f.dic },
    { label: 'Zápis v OR', value: f.zapis_or },
    {
      label: 'Pobočka',
      value: [f.pobocka_nazev, f.pobocka_adresa].filter(Boolean).join(', '),
    },
    { label: 'Banka', value: f.banka_nazev },
    { label: 'Číslo účtu', value: f.banka_ucet },
    { label: 'IBAN', value: f.banka_iban },
    { label: 'SWIFT', value: f.banka_swift },
    { label: 'E-mail', value: f.email },
    { label: 'Telefon', value: f.telefon },
    { label: 'Web', value: f.web },
    { label: 'Poznámka', value: f.poznamka },
  ];
  return rows.filter((r) => r.value.trim());
}

/** Celý blok fakturačních údajů jako text do schránky. */
export function firmaText(f: MojeFirmaData): string {
  return firmaRows(f)
    .map((r) => (r.label === 'Název' ? r.value : `${r.label}: ${r.value}`))
    .join('\n');
}

export async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Starší prohlížeče / nedostupné clipboard API — fallback přes dočasné pole.
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    if (!ok) throw new Error('copy failed');
  }
}

/** Naposledy zobrazená firma v „Moje údaje" (jen pohodlí tohoto prohlížeče). */
const LAST_KEY = 'sd_moje_firma_last';
export function loadLastFirmaId(): number | null {
  try {
    const v = localStorage.getItem(LAST_KEY);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}
export function saveLastFirmaId(id: number): void {
  try {
    localStorage.setItem(LAST_KEY, String(id));
  } catch {
    /* nevadí */
  }
}
